/**
 * Feature Flags (P5 Wave 4 §5) — READ-ONLY.
 *
 * A grid of FEATURE_* flag cards grouped by Quill service, read live from each
 * service's RUNNING ECS task definition. There is intentionally NO toggle:
 * flipping a Quill feature flag is a DEPLOY (a task-definition env change via
 * `set_feature_flag.sh`), not a database write — so this page surfaces the flag
 * state plus the exact command to change it, and nothing here mutates anything.
 *
 * States: loading (skeleton cards), empty (no flags found), and error + Retry.
 * A 503 ECS_READ_FAILED (the backend couldn't read ECS) renders as a clear
 * error with Retry rather than being silently shown as "no flags".
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Button,
  Skeleton,
  Tooltip,
  IconButton,
} from '@mui/material';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import TerminalIcon from '@mui/icons-material/Terminal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuillHealth } from '../QuillHealthContext';
import EmptyState from '../../common/EmptyState';
import { colors, typography } from '../../../theme/designTokens';
import { flagsApi, FeatureFlagServiceGroup, FeatureFlag, QuillApiError } from './api';

/** A flag env value counts as "on" only for the canonical truthy strings. */
function isFlagOn(value: string | null): boolean {
  const v = (value || '').trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'on' || v === 'yes' || v === 'enabled';
}

/** True when the flag is known to the code but has no entry on the task definition. */
function isUnset(flag: FeatureFlag): boolean {
  return flag.source === 'unset' || flag.value === null;
}

/** The exact deploy command to flip a flag — mirrors the ops `set_feature_flag.sh`. */
function flagCommand(flag: FeatureFlag): string {
  // For an unset flag the useful command is the one that turns it ON: there is
  // nothing to toggle off.
  if (isUnset(flag)) return `set_feature_flag.sh ${flag.name} true`;
  return `set_feature_flag.sh ${flag.name} ${isFlagOn(flag.value) ? 'false' : 'true'}`;
}

const MonoCommand: React.FC<{ command: string }> = ({ command }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard?.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the command is still visible to copy manually */
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 1,
        px: 1,
        py: 0.5,
        borderRadius: '8px',
        backgroundColor: colors.gray[900],
        overflowX: 'auto',
      }}
    >
      <Typography
        component="code"
        sx={{
          fontFamily: typography.fonts.mono,
          fontSize: typography.sizes.xs,
          color: colors.gray[100],
          whiteSpace: 'nowrap',
        }}
      >
        {command}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title={copied ? 'Copied' : 'Copy command'}>
        <IconButton size="small" onClick={copy} sx={{ color: colors.gray[300] }} aria-label={`Copy ${command}`}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const FlagCard: React.FC<{ flag: FeatureFlag }> = ({ flag }) => {
  const unset = isUnset(flag);
  const on = !unset && isFlagOn(flag.value);
  // F15: three states, not two. "OFF (not set)" is visually distinct from an
  // explicit "false" — the service falls back to its own code default, which is
  // a different fact from an operator having deliberately turned it off.
  const label = unset ? 'OFF (not set)' : on ? 'ON' : 'OFF';
  const chipSx = unset
    ? {
        backgroundColor: 'transparent',
        color: colors.gray[500],
        border: `1px dashed ${colors.gray[400]}`,
      }
    : {
        backgroundColor: on ? colors.success[50] : colors.gray[100],
        color: on ? colors.success.dark : colors.gray[600],
        border: `1px solid ${on ? colors.success.light : colors.gray[300]}`,
      };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: '16px',
        height: '100%',
        borderColor: colors.gray[200],
        opacity: unset ? 0.75 : 1,
      }}
      data-testid="flag-card"
      data-flag-source={unset ? 'unset' : 'task_definition'}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Typography
            sx={{
              fontFamily: typography.fonts.mono,
              fontSize: typography.sizes.sm,
              fontWeight: 600,
              wordBreak: 'break-all',
              flexGrow: 1,
            }}
          >
            {flag.name}
          </Typography>
          <Chip label={label} size="small" sx={{ fontWeight: 700, letterSpacing: '0.5px', ...chipSx }} />
        </Box>

        {flag.effect && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {flag.effect}
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {unset ? (
            <>not set on the task definition — the service uses its own default</>
          ) : (
            <>
              value:{' '}
              <Box component="span" sx={{ fontFamily: typography.fonts.mono }}>
                {flag.value || '(empty)'}
              </Box>
            </>
          )}
        </Typography>

        {flag.unknown_to_manifest && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
            Set here, but not one of the names quill-engine is known to read.
          </Typography>
        )}

        <MonoCommand command={flagCommand(flag)} />
      </CardContent>
    </Card>
  );
};

const ServiceGroup: React.FC<{ group: FeatureFlagServiceGroup }> = ({ group }) => {
  const setCount = group.flags.filter((f) => !isUnset(f)).length;
  const unsetCount = group.flags.length - setCount;
  const unreadable = group.task_definition_read === false;
  return (
  <Box sx={{ mb: 4 }} data-testid="service-group">
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Typography
        sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}
      >
        {group.service}
      </Typography>
      <Chip label={`${setCount} set`} size="small" variant="outlined" />
      {unsetCount > 0 && (
        <Chip label={`${unsetCount} not set`} size="small" variant="outlined" sx={{ borderStyle: 'dashed' }} />
      )}
    </Box>
    {group.flags.length === 0 ? (
      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
        {unreadable
          ? "This service's task definition could not be read, so nothing is claimed about its flags."
          : "No FEATURE_* flags on this service's task definition."}
      </Typography>
    ) : (
      <Grid container spacing={2}>
        {group.flags.map((flag) => (
          <Grid item xs={12} sm={6} md={4} key={`${group.service}:${flag.name}`}>
            <FlagCard flag={flag} />
          </Grid>
        ))}
      </Grid>
    )}
  </Box>
  );
};

const FeatureFlagsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const [services, setServices] = useState<FeatureFlagServiceGroup[]>([]);
  const [readAt, setReadAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await flagsApi.getFlags();
      if (!mountedRef.current) return;
      setServices(res.services ?? []);
      setReadAt(res.read_at ?? null);
    } catch (e) {
      if (!mountedRef.current) return;
      const msg =
        e instanceof QuillApiError && e.code === 'ECS_READ_FAILED'
          ? `Couldn't read the live ECS task definitions (${e.message}). The flag state can't be shown right now.`
          : e instanceof Error
            ? e.message
            : 'Failed to load feature flags.';
      setError(msg);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalFlags = services.reduce((sum, s) => sum + s.flags.length, 0);
  const hasAnyFlags = totalFlags > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <OutlinedFlagIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
            Feature Flags
          </Typography>
          <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
            Live FEATURE_* flags read from each Quill service's running ECS task definition. Read-only.
          </Typography>
        </Box>
      </Box>

      {/* Connection state */}
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Quill backend • {status === 'up' ? 'connected' : status}
        {readAt && ` · read ${new Date(readAt).toLocaleString()}`}
      </Typography>

      {/* Deploy-not-toggle callout — flipping a flag is a deploy, never a UI toggle. */}
      <Alert
        icon={<TerminalIcon />}
        severity="info"
        sx={{ mb: 3, borderRadius: '12px', alignItems: 'flex-start' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Flipping a flag is a deploy, not a switch.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          These values live in the ECS task-definition env. Change one by running the ops script and redeploying the
          service — there is no write path from this dashboard. Flags shown as{' '}
          <strong>OFF (not set)</strong> are names the code branches on that have no entry on the
          running task definition, so the service falls back to its own default.
        </Typography>
        <Box
          component="code"
          sx={{
            display: 'inline-block',
            fontFamily: typography.fonts.mono,
            fontSize: typography.sizes.xs,
            backgroundColor: colors.gray[900],
            color: colors.gray[100],
            px: 1,
            py: 0.5,
            borderRadius: '6px',
          }}
        >
          set_feature_flag.sh FEATURE_X true
        </Box>
      </Alert>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
            </Grid>
          ))}
        </Grid>
      ) : !hasAnyFlags ? (
        <EmptyState
          icon="flag"
          title="No feature flags found"
          description="No FEATURE_* env vars are set on the running Quill task definitions, or the services aren't deployed yet."
        />
      ) : (
        services.map((group) => <ServiceGroup key={group.service} group={group} />)
      )}
    </Box>
  );
};

export default FeatureFlagsPage;
